import { useState, useRef, useEffect } from "react";
import { useAppStore } from "../stores/useAppStore";
import IconArrow from "./IconArrow";
import Button from "./Button";
import { motion } from "motion/react";
import AnalyticsWrapper from "./AnalyticsWrapper";
import CardCarousel from "./CardCarousel";
import Icon1 from "./Icons/Icon1";
import Icon2 from "./Icons/Icon2";
import Icon3 from "./Icons/Icon3";
import Icon4 from "./Icons/Icon4";
import Icon5 from "./Icons/Icon5";
import Icon6 from "./Icons/Icon6";
import { toUrlFriendly } from "../utils/utils";

export default function Carousel({ items, initialSlide = 0, itemWidth, itemHeight, renderItem, SlideComponent, section, loop = true }) {
  const { content, cardTouchPos, menuItems } = useAppStore();

  const [currentIndex, setCurrentIndex] = useState(initialSlide);
  const [dragging, setDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const containerRef = useRef(null);

  const { closeCarousel } = useAppStore((state) => state.carousel);
  const { setCardTouchPos } = useAppStore();

  const [title, setTitle] = useState('');
  const [currentItem, setCurrentItem] = useState(null);

  useEffect(() => {
    setCurrentIndex(initialSlide);
  }, [initialSlide]);

  useEffect(() => {
    const currentItem = items[currentIndex];
    const subsection = toUrlFriendly(content?.lets_talk_strategy?.items[currentIndex]?.category);
    AnalyticsWrapper.trackPageView(`/${section}/${subsection}`);

    setTitle(currentItem?.category || '');
    setCurrentItem(currentItem);
  }, [currentIndex, items]);

  const handleStart = (x) => {
    setDragging(true);
    setStartX(x);
    setDragOffset(0);
  };

  const handleMove = (x) => {
    if (!dragging) return;
    const delta = x - startX;
    setDragOffset(delta);
  };

  const handleEnd = () => {
    if (!dragging) return;
    setDragging(false);

    if (dragOffset > 100) {
      prevSlide();
    } else if (dragOffset < -100) {
      nextSlide();
    }

    setDragOffset(0);
  };

  const handleButtonBackClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    let x, y;

    // Para eventos táctiles, usar changedTouches en lugar de touches
    if (event.changedTouches && event.changedTouches.length > 0) {
      x = event.changedTouches[0].clientX;
      y = event.changedTouches[0].clientY;
    } else if (event.touches && event.touches.length > 0) {
      x = event.touches[0].clientX;
      y = event.touches[0].clientY;
    } else {
      // Evento de ratón
      x = event.clientX;
      y = event.clientY;
    }
    setCardTouchPos({x, y});

    closeCarousel();
  };

  // Eventos de ratón
  const handleMouseDown = (e) => handleStart(e.clientX);
  const handleMouseMove = (e) => handleMove(e.clientX);
  const handleMouseUp = () => handleEnd();
  const handleMouseLeave = () => setDragging(false);

  // Eventos táctiles
  const handleTouchStart = (e) => handleStart(e.touches[0].clientX);
  const handleTouchMove = (e) => handleMove(e.touches[0].clientX);
  const handleTouchEnd = (e) => handleEnd();

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => {
      if (loop) {
        return (prevIndex + 1) % items.length;
      } else {
        return prevIndex < items.length - 1 ? prevIndex + 1 : prevIndex;
      }
    });
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => {
      if (loop) {
        return prevIndex === 0 ? items.length - 1 : prevIndex - 1;
      } else {
        return prevIndex > 0 ? prevIndex - 1 : prevIndex;
      }
    });
  };

  const getSlideStyles = (index) => {
    const offset = index - currentIndex;

    const normalizedOffset =
      offset < -Math.floor(items.length / 2)
        ? offset + items.length
        : offset > Math.floor(items.length / 2)
        ? offset - items.length
        : offset;

    const distance =
      normalizedOffset * 100 + (dragOffset / window.innerWidth) * 100;

    const proximity = Math.max(0, 1 - Math.abs(distance / 300));
    const scale = 0.6 + proximity * 0.4;
    const zIndex = Math.round(proximity * 10);
    const opacity = 0.5 + proximity * 0.5;
    const blurAmount = 14 * (1 - proximity);

    const transform = `translate3d(${distance}%, 0, 0) scale(${scale})`;

    return {
      transform,
      zIndex,
      opacity,
      filter: `blur(${blurAmount}px)`,
      transition:
        dragging || Math.abs(normalizedOffset) > 1 ? "none" : "transform 0.5s ease",
    };
  };

  const prevIndex = currentIndex === 0 ? items.length - 1 : currentIndex - 1;
  const nextIndex = (currentIndex + 1) % items.length;

  const variants = {
    closed: {
        backgroundColor: '#1a2951',
        clipPath: `circle(0 at ${cardTouchPos.x}px ${cardTouchPos.y}px)`,
        pointerEvents: 'none',
        transition: {
            duration: 0.5,
            ease: "easeInOut",
            delay: 0.1
        },
    },
    open: {
        backgroundColor: '#07182D',
        clipPath: `circle(140% at ${cardTouchPos.x}px ${cardTouchPos.y}px)`,
        pointerEvents: 'auto',
        transition: {
            duration: 0.7,
            ease: "easeInOut",
            delay: 0.2
        },
    },
};

  return (
    <motion.div
      className="fixed inset-0 z-20 transform-gpu will-change-clip-path"
      variants={variants}
      initial="closed"
      animate="open"
      exit="closed"
    >
      <div className='absolute aspect-16-9:top-320u not-aspect-16-9:top-400u left-1/2 -translate-x-1/2 z-30
      flex flex-row items-center'>
        <div className={`
        flex
        w-110u h-110u rounded-full
        font-normal text-[6.375rem] leading-none
        bg-${currentItem?.color || '03BCE9'}
        justify-center items-center
        mr-90u
        relative`}
        >
          <div className={`absolute w-150u h-150u rounded-full
          bg-${currentItem?.color === 'navy' ? 'white' : currentItem?.color} opacity-40 -z-10`}></div>
            {currentIndex === 0 && (
            <Icon1 className="w-70u h-auto" />
            )}
            {currentIndex === 1 && (
            <Icon2 className="w-80u h-auto" />
            )}
            {currentIndex === 2 && (
            <Icon3 className="w-70u h-auto" />
            )}
            {currentIndex === 3 && (
            <Icon4 className="w-80u h-auto" />
            )}
            {currentIndex === 4 && (
            <Icon5 className="w-65u h-auto" />
            )}
            {currentIndex === 5 && (
            <Icon6 className="w-70u h-auto" style={{filter: 'brightness(0) saturate(100%) invert(8%) sepia(37%) saturate(1299%) hue-rotate(194deg) brightness(93%) contrast(95%)'}} />
            )}
        </div>
        <div className='text-120u font-extralight'>{title}</div>
      </div>
      <div
        className="w-full h-full flex items-center justify-center"
        variants={variants}
        initial="closed"
        animate="open"
        exit="closed"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        ref={containerRef}
      >
        <div className="relative w-full h-full flex overflow-hidden items-center justify-center">
          {items.map((card, index) => {
            return (
            <div
                key={card.id}
                className="absolute rounded-lg flex
                mt-151u
                items-center justify-center text-xl font-bold transform-gpu"
                style={{
                  ...getSlideStyles(index),
                  ...(itemHeight ? { height: itemHeight } : {}),
                  ...(itemWidth ? { width: itemWidth } : {width: '168rem'}),
                }}
              >
                {renderItem
                  ? renderItem(card, index)
                  : SlideComponent
                  ? <SlideComponent item={card} index={index} className="h-full" />
                  : <CardCarousel item={card} />
                }
              </div>
            );
          })}
        </div>
      </div>

      {/* Prev button - solo si loop o no estamos en el primer slide */}
      {(loop || currentIndex > 0) && (
        <Button
          onClick={prevSlide}
          className="click-effect absolute py-[2.75rem] pl-66u pr-52u
          right-632u bottom-108u z-50
          w-458u
          justify-between
          text-32u
          bg-[#07182D]"
        >
          <IconArrow className="w-[1.125rem] h-auto transform -scale-x-100" />
          <div>Previous topic</div>
        </Button>
      )}
      {/* Next button - solo si loop o no estamos en el último slide */}
      {(loop || currentIndex < items.length - 1) && (
        <Button
          onClick={nextSlide}
          className="
          click-effect absolute py-[2.75rem] pr-66u pl-52u
          right-123u bottom-108u
          z-50
          w-458u
          justify-between
          text-32u
          bg-[#07182D]
          "
        >
          <div>Next topic</div>
          <IconArrow className="w-[1.125rem] h-auto" />
        </Button>
      )}

      <Button
          onClick={(event) => handleButtonBackClick(event)}
          className="click-effect absolute py-[2.75rem] pl-66u pr-52u
          left-138u bottom-108u z-50
          w-458u
          justify-between
          text-32u
          bg-[#07182D]"
        >
          <IconArrow className="w-[1.125rem] h-auto transform -scale-x-100" />
          <div>Back to all topics</div>
        </Button>
    </motion.div>
  );
};